import React, { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { Link } from "react-router-dom";
import { FileSpreadsheet, FileText, FileType, Filter, Users2 } from "lucide-react";

const STATUSES = ["New Case", "Work In Progress", "Completed", "Rejected", "On Hold"];

export default function ConnectorReports() {
  const [rows, setRows] = useState([]);
  const [services, setServices] = useState([]);
  const [service, setService] = useState("");
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (service) params.service = service;
    if (status) params.status = status;
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    const r = await api.get("/reports/connectors", { params });
    setRows(r.data);
    setLoading(false);
  };

  useEffect(() => { api.get("/meta").then((r) => setServices(r.data.service_types)); load(); /* eslint-disable-next-line */ }, []);

  const exportCsv = () => {
    const header = ["Connector", "Mobile", "Total", "Approved", "Rejected", "Pending", "In Process", "Business (₹)", "Commission (₹)"];
    const lines = [header.join(",")];
    rows.forEach((r) => lines.push([r.connector_name, r.connector_phone, r.total_leads, r.approved, r.rejected, r.pending, r.in_process, r.total_business, r.commission_earned].map((v) => `"${v ?? ""}"`).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `connector_reports_${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportServer = async (fmt) => {
    const token = localStorage.getItem("crm_token");
    const p = new URLSearchParams({ fmt });
    if (service) p.set("service", service);
    if (status) p.set("status", status);
    if (from) p.set("date_from", from);
    if (to) p.set("date_to", to);
    const res = await fetch(`${API_BASE}/reports/export?${p}`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
    const blob = await res.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `connector_report.${fmt}`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="connector-reports-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold flex items-center gap-2"><Users2 size={26} /> Connector Reports</h1>
        <p className="text-sm text-muted-foreground">Performance and business summary for every connector</p>
      </div>

      <div className="card p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1"><Filter size={12} /> Service</label>
          <select className="input mt-1" value={service} onChange={(e) => setService(e.target.value)} data-testid="cr-service">
            <option value="">All</option>{services.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground">Status</label>
          <select className="input mt-1" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="cr-status">
            <option value="">All</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div><label className="text-xs font-semibold text-muted-foreground">From</label>
          <input type="date" className="input mt-1" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="cr-from" /></div>
        <div><label className="text-xs font-semibold text-muted-foreground">To</label>
          <input type="date" className="input mt-1" value={to} onChange={(e) => setTo(e.target.value)} data-testid="cr-to" /></div>
        <button onClick={load} className="btn btn-primary" data-testid="cr-apply">Apply</button>
        <div className="flex-1" />
        <button onClick={exportCsv} className="btn btn-outline" data-testid="cr-csv"><FileType size={14} /> CSV</button>
        <button onClick={() => exportServer("xlsx")} className="btn btn-outline" data-testid="cr-xlsx"><FileSpreadsheet size={14} /> Excel</button>
        <button onClick={() => exportServer("pdf")} className="btn btn-outline" data-testid="cr-pdf"><FileText size={14} /> PDF</button>
      </div>

      <div className="card overflow-hidden">
        <table className="table-clean">
          <thead>
            <tr><th>Connector</th><th>Mobile</th><th>Total</th><th>Approved</th><th>Rejected</th><th>Pending</th><th>In Process</th><th>Business (₹)</th><th>Commission (₹)</th><th className="text-right">Detail</th></tr>
          </thead>
          <tbody data-testid="cr-body">
            {loading && <tr><td colSpan={10} className="text-center py-8 text-muted-foreground">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-muted-foreground">No connectors</td></tr>}
            {rows.map((r) => (
              <tr key={r.connector_id}>
                <td className="font-semibold">{r.connector_name}<div className="text-xs text-muted-foreground font-mono">@{r.connector_username}</div></td>
                <td className="tabular">{r.connector_phone || "—"}</td>
                <td className="tabular font-semibold">{r.total_leads}</td>
                <td><span className="badge badge-green">{r.approved}</span></td>
                <td><span className="badge badge-red">{r.rejected}</span></td>
                <td><span className="badge badge-blue">{r.pending}</span></td>
                <td><span className="badge badge-yellow">{r.in_process}</span></td>
                <td className="tabular">₹{r.total_business?.toFixed?.(2) ?? 0}</td>
                <td className="tabular">₹{r.commission_earned?.toFixed?.(2) ?? 0}<div className="text-[10px] text-muted-foreground">{r.commission_rate ? `${r.commission_rate}%` : "no rate"}</div></td>
                <td className="text-right"><Link to={`/reports/connectors/${r.connector_id}`} className="link-blue text-sm" data-testid={`cr-detail-${r.connector_username}`}>View</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
